import React, { useState, useEffect } from "react";
import { Check, CreditCard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, startGoogleLogin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const PLANS = [
  {
    key: "free",
    lookup_key: null,
    price: "$0",
    cadence: "forever",
    name: "Reader",
    tagline: "Everything you need, unlimited.",
    features: [
      "Unlimited summaries",
      "URL · PDF · Text · YouTube",
      "Claude Sonnet 4.5 & Gemini 3.1 Pro",
      "EN ⇄ VI translation",
      "Lumi chat bot",
    ],
  },
  {
    key: "deeper",
    lookup_key: "deeper_monthly",
    price: "$8",
    cadence: "/month",
    name: "Deeper",
    tagline: "For power readers doing serious work.",
    highlight: true,
    features: [
      "Everything in Reader",
      "Meeting mode (mic + Whisper)",
      "Long PDFs up to 100 pages",
      "Markdown export",
      "Priority processing",
    ],
  },
  {
    key: "studio",
    lookup_key: "studio_monthly",
    price: "$15",
    cadence: "/month",
    name: "Studio",
    tagline: "For teams that read together.",
    features: [
      "Everything in Deeper",
      "Shareable summary links",
      "Team library",
      "API access (soon)",
      "Premium support",
    ],
  },
];

export default function PricingSection() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [busyKey, setBusyKey] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/subscription/me").then(({ data }) => setCurrentPlan(data?.plan || "free")).catch(() => {});
  }, [user]);

  const subscribe = async (lookup_key) => {
    if (!user) { startGoogleLogin(); return; }
    if (!lookup_key) { navigate("/dashboard"); return; }
    setBusyKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 py-24 relative" data-testid="pricing-section">
      <div className="max-w-2xl mb-14">
        <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-3">
          {lang === "vi" ? "Bảng giá" : "Pricing"}
        </p>
        <h2 className="text-4xl sm:text-5xl leading-tight mb-4">
          {lang === "vi" ? "Miễn phí. Nâng cấp khi cần chiều sâu." : "Free. Upgrade only when you need depth."}
        </h2>
        <p className="text-muted-foreground text-lg leading-relaxed">
          {lang === "vi"
            ? "Bản Reader miễn phí không giới hạn. Nâng cấp khi bạn cần chế độ họp, PDF dài hoặc chia sẻ nhóm."
            : "Reader is unlimited and free. Upgrade when you need meeting mode, longer PDFs, or team sharing."}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {PLANS.map((p) => (
          <div
            key={p.key}
            data-testid={`plan-${p.key}`}
            className={`relative rounded-3xl p-8 border ${p.highlight ? "border-primary/60 bg-primary/[0.04] shadow-lg shadow-primary/10" : "border-border bg-card"} flex flex-col`}
          >
            {p.highlight && (
              <span className="absolute -top-3 left-8 text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full bg-primary text-primary-foreground">
                {lang === "vi" ? "Được chọn nhiều" : "Most popular"}
              </span>
            )}
            <p className="text-muted-foreground text-xs tracking-[0.22em] uppercase mb-4">{p.name}</p>
            <div className="flex items-baseline gap-2 mb-2">
              <p className="font-display text-5xl">{p.price}</p>
              <p className="text-muted-foreground text-sm">{p.cadence}</p>
            </div>
            <p className="text-sm text-muted-foreground mb-6">{p.tagline}</p>
            <ul className="space-y-3 mb-8 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check size={16} className="text-primary mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              data-testid={`plan-cta-${p.key}`}
              onClick={() => subscribe(p.lookup_key)}
              disabled={!!busyKey || currentPlan === p.key}
              className={`w-full h-12 rounded-full ${p.highlight ? "" : "bg-secondary text-foreground hover:bg-accent"}`}
              variant={p.highlight ? "default" : "secondary"}
            >
              {busyKey === p.lookup_key ? (
                <><Loader2 className="animate-spin mr-2" size={16} /> {lang === "vi" ? "Đang mở…" : "Opening…"}</>
              ) : currentPlan === p.key ? (
                lang === "vi" ? "Đang dùng" : "Current plan"
              ) : p.key === "free" ? (
                user ? (lang === "vi" ? "Mở bảng điều khiển" : "Open dashboard") : (lang === "vi" ? "Bắt đầu miễn phí" : "Start for free")
              ) : (
                <><CreditCard size={16} className="mr-2" /> {lang === "vi" ? "Nâng cấp" : "Upgrade"}</>
              )}
            </Button>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground text-center mt-8 flex items-center justify-center gap-2 flex-wrap">
        <CreditCard size={13} />
        {lang === "vi"
          ? "Thanh toán an toàn qua Stripe · Chấp nhận Visa, Mastercard, Amex · Huỷ bất cứ lúc nào"
          : "Secure checkout by Stripe · Visa · Mastercard · Amex · Cancel anytime"}
      </p>
    </section>
  );
}
